import React, { useContext } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';

import SVG from 'components/base/SVG';
import MatchTooltip from './MatchTooltip';

import {
  getInvestorTypeColor,
  getInvestorTypeIcon,
  getInvestorTypeLabel,
  getInvestorTypeBackground,
} from '../investorTypeTheme';
import {
  formatProperties,
  formatPurchases,
  formatCompactCurrency,
  formatOwnership,
  formatMatchScore,
  formatInvestorMatchSummary,
} from 'data/investors';

import SaveInvestorButton from '../SaveInvestorButton';
import useSaveInvestor from '../useSaveInvestor';
import useSkipTraceInvestors from '../useSkipTraceInvestors';
import useDialInvestorPhone from '../useDialInvestorPhone';
import SelectCheckbox from './SelectCheckbox';
import SelectionContext from './SelectionContext';
import OwnedProperties from './OwnedProperties';
import css from './style.scss';
import InvestorProfile from '../Profile';
import { ContactPerson } from '../Profile/ContactInfo';

// A single investor result card — Figma node 34:3141 ("investor card"), in the
// "Search Result - not skiptraced" state (node 3:15998).
//
// Metric fields come from the service's `metrics` block:
//   Portfolio Size  -> metrics.portfolioSize
//   Last 12 Months  -> metrics.acquisitionCountLast12Months
//   Avg Purchase    -> metrics.avgPurchasePrice
//   Ownership       -> metrics.avgOwnershipLengthMonths
//
// Badge colour and icon come from the per-type maps in ../investorTypeTheme.
// Types without a matching glyph render colour-only rather than borrowing an
// icon that would misdescribe them.
const TypeTag = ({ type }) => {
  const color = getInvestorTypeColor(type);
  const background = getInvestorTypeBackground(type);
  const icon = getInvestorTypeIcon(type);

  return (
    <li className={css.tag} style={{ color, backgroundColor: background }}>
      {!icon ? null : (
        <span className={css.tag__iconBox}>
          <SVG icon={icon} className={css.tag__glyph} />
        </span>
      )}
      {getInvestorTypeLabel(type)}
    </li>
  );
};

const Metric = ({ label, value, className }) => (
  <div className={classNames(css.metric, className)}>
    <span className={css.metric__label}>{label}</span>
    <span className={css.metric__value}>{value}</span>
  </div>
);

// Reads the selection from context so the card body above it doesn't have to
// re-render when only the checked state flips (Select all = 105 of these).
const CardCheckbox = ({ investor, onToggle }) => {
  const selectedIds = useContext(SelectionContext);

  return (
    <SelectCheckbox
      id={`investor-select-${investor.id}`}
      checked={selectedIds.has(investor.id)}
      onChange={() => onToggle(investor.id)}
      label={`Select ${investor.name || 'investor'}`}
    />
  );
};

const InvestorCard = ({ investor, rank, rankLabel, onToggle, onSkipTraced, onContactsUpdated }) => {
  const metrics = investor.metrics || {};
  const classifications = investor.classifications || [];
  const matchScore = formatMatchScore(investor.matchScore);
  const matchSummary = formatInvestorMatchSummary(investor);
  const dispatch = useDispatch();
  // "save investor" (I34:3141;1047:55170), 30x30 — the card's own bookmark,
  // not opened from inside another popup, so no priority override is needed
  // (see ../useSaveInvestor for why the Profile header's copy needs one).
  const { saved, toggleSave } = useSaveInvestor(investor);
  const { skipTrace, materializing } = useSkipTraceInvestors();
  // Search results are a one-time snapshot fetched by Results/index.js and
  // never self-update on their own. Re-fetching just this investor's
  // *profile* after a Skip Trace looked tempting but is wrong: getProfile
  // reads a snapshot that's only persisted when something explicitly saves
  // one (e.g. "Add to Investor List") -- a plain search result has none, so
  // that 404s ("Not Found" at /ps-core/investors/{id}) for exactly the
  // investors this card is showing. The search endpoint has no such
  // dependency (stateless, live), so onSkipTraced re-runs it instead --
  // shared with the bulk "Actions -> Skip Trace" path (Results/index.js),
  // which needs the same refresh for however many cards were selected.
  const contacts = investor.contacts || [];
  // ATAI-373: having *any* contacts isn't the same as having been skip
  // traced -- a manually-added phone/email (Profile/EditContactModal's own
  // `source: 'MANUAL'`) used to be enough on its own to flip this card into
  // its "skip traced" look and hide the Skip Trace button, even though no
  // skip trace had actually run. See Profile/ContactInfo.js's identical fix
  // for the full reasoning -- showing the contact list and the Skip Trace
  // CTA are no longer mutually exclusive below.
  const hasContacts = Boolean(contacts.length);
  const hasSkipTracedContact = contacts.some(
    (contact) =>
      (contact.phones || []).some((phone) => phone.source !== 'MANUAL') ||
      (contact.emails || []).some((email) => email.source !== 'MANUAL')
  );

  // click-to-dial on the card's phone numbers (see ../useDialInvestorPhone).
  const dialPhone = useDialInvestorPhone(investor);

  return (
    <li className={css.card}>
      <div className={css.card__top}>
        <span className={css.rank}>{rankLabel ? `#${rank} ${rankLabel}` : `#${rank}`}</span>
        <div className={css.card__topActions}>
          <SaveInvestorButton
            saved={saved}
            onToggle={toggleSave}
            className={css.saveButton}
            iconClassName={css.saveButton__icon}
          />
          <CardCheckbox investor={investor} onToggle={onToggle} />
        </div>
      </div>

      <div className={css.card__identity}>
        <div className={css.identity}>
          <p className={css.name} title={investor.name}>
            {investor.name || 'Unknown investor'}
          </p>
          {!classifications.length && !matchScore ? null : (
            <ul className={css.tags}>
              {classifications.map((tag) => (
                <TypeTag key={tag} type={tag} />
              ))}
              {/* The service returns a real score now, so the design's match
                  tag is shown rather than omitted. On hover it explains the
                  score with a generated "why this match" description (Figma
                  3:262) — the whole pill is the hover target because the
                  Tooltip's trigger wraps it. */}
              {!matchScore ? null : (
                <li className={css.tagMatchItem}>
                  {!matchSummary ? (
                    <span className={css.tagMatch}>{matchScore}</span>
                  ) : (
                    <MatchTooltip text={matchSummary}>
                      <span className={css.tagMatch}>{matchScore}</span>
                    </MatchTooltip>
                  )}
                </li>
              )}
            </ul>
          )}
        </div>
        <button
          type="button"
          className={css.ghostButton}
          onClick={() => dispatch(InvestorProfile.open({ investor, onContactsUpdated }))}
        >
          View Details
        </button>
      </div>

      <div className={css.metrics}>
        <Metric
          label="Portfolio Size"
          value={formatProperties(metrics.portfolioSize)}
          className={css.metricPortfolio}
        />
        <Metric
          label="Last 12 Months"
          value={formatPurchases(metrics.acquisitionCountLast12Months)}
        />
        <Metric
          label="Avg Purchase Price"
          value={formatCompactCurrency(metrics.avgPurchasePrice)}
          className={css.metricAvgPurchase}
        />
        <Metric
                  label="Ownership"
                  value={formatOwnership(metrics.avgOwnershipLengthMonths)}
                  className={css.metricOwnership}
                />
      </div>

      {!hasContacts ? null : (
        <div className={css.contactList}>
          {contacts.map((contact, index) => (
            <ContactPerson
              key={contact.name || index}
              contact={contact}
              index={index}
              onPhoneClick={dialPhone}
            />
          ))}
        </div>
      )}

      {hasSkipTracedContact ? null : (
        <div className={css.contact}>
          <div className={css.contact__copy}>
            <p className={css.contact__title}>Contact Information</p>
            <p className={css.contact__body}>
              Access the contact information for this investor at no cost.
            </p>
          </div>
          <button
            type="button"
            className={css.primaryButton}
            disabled={materializing}
            onClick={() => skipTrace([investor], { onSuccess: onSkipTraced })}
          >
            <SVG icon="iconInvestorSkipTrace" className={css.primaryButton__icon} />
            Skip Trace
          </button>
        </div>
      )}

      <OwnedProperties
        properties={investor.properties}
        currentCount={investor.currentPropertyCount}
        onViewMore={() => dispatch(InvestorProfile.open({ investor, onContactsUpdated }))}
      />
    </li>
  );
};

// Memoized: Results hands every card stable props (selection goes through
// SelectionContext, not a prop), so a selection change leaves the card bodies
// alone and only re-renders their checkboxes.
export default React.memo(InvestorCard);
